import { TWO, ZERO, MAXIMUM_BADGE_NOTIFICATIONS_NUMBER, PRESENTER_TITLE } from '../../constans/common-constants';

class Badge {
  private value: string;
  private canvas: HTMLCanvasElement;
  private src: string | HTMLElement | null;
  private faviconEL: HTMLElement | null = document.querySelector('link[rel$=icon]');
  private ctx: CanvasRenderingContext2D | null;
  private faviconSize: number;
  private radius: number;
  private offset: Record<string, number>;
  private badgeSize: number;
  private backgroundColor: string | CanvasGradient | CanvasPattern;
  private color: string | CanvasGradient | CanvasPattern;
  private img: CanvasImageSource;
  private size: number;
  private position: string;
  private onChange: () => void;

  private rect: number;
  private marginScale: number;
  private fontScale: number;
  private radix: number;

  constructor () {
    Object.assign(
      this, {
        backgroundColor: '#f00',
        color: '#fff',
        size: 0.7, // 0..1 (Scale in respect to the favicon image size)
        position: 'ne', // Position inside favicon "n", "e", "s", "w", "ne", "nw", "se", "sw"
        radius: 35, // Border radius
        src: '', // Favicon source (dafaults to the <link> icon href)
        onChange () {},
      },
    );

    this.canvas = document.createElement('canvas');

    if(this.faviconEL) {
      this.src = this.src || this.faviconEL.getAttribute('href');
    }

    this.ctx = this.canvas.getContext('2d');

    this.rect = 12;
    this.marginScale = 0.18;
    this.fontScale = 0.82;
    this.radix = 10;
  }

  get dataURL (): string {
    return this.canvas.toDataURL();
  }

  public update (): void {
    const initalMinValue = 99;
    this.value = Math.min(initalMinValue, parseInt(this.value, this.radix)).toString();

    if (this.img) {
      this.draw();
      if (this.onChange) this.onChange.call(this);
    } else {
      this.img = new Image();
      this.img.addEventListener('load', () => {
        this.setup();
        this.draw();
        if (this.onChange) this.onChange.call(this);
      });

      // @ts-ignore
      this.img.src = this.src;
    }
  }

  public getValue (): string {
    return this.value;
  }

  public setValue (val: string): void {
    this.value = val;
    this.update();
  }

  private drawFullSizeIcon (): void {
    if(!this.ctx) return;
    this.ctx.clearRect(ZERO, ZERO, this.faviconSize, this.faviconSize);
    this.ctx.drawImage(this.img, ZERO, ZERO, this.faviconSize, this.faviconSize);
  }

  private drawIcon (): void {
    if(!this.ctx) return;
    this.ctx.clearRect(ZERO, ZERO, this.faviconSize, this.faviconSize);
    this.ctx.drawImage(this.img, ZERO, this.rect, this.faviconSize - this.rect, this.faviconSize - this.rect);
  }

  private drawShape (): void {
    if(!this.ctx) return;
    const r = this.radius;
    const xa = this.offset.x;
    const ya = this.offset.y;
    const xb = this.offset.x + this.badgeSize;
    const yb = this.offset.y + this.badgeSize;
    this.ctx.beginPath();
    this.ctx.moveTo(xb - r, ya);
    this.ctx.quadraticCurveTo(xb, ya, xb, ya + r);
    this.ctx.lineTo(xb, yb - r);
    this.ctx.quadraticCurveTo(xb, yb, xb - r, yb);
    this.ctx.lineTo(xa + r, yb);
    this.ctx.quadraticCurveTo(xa, yb, xa, yb - r);
    this.ctx.lineTo(xa, ya + r);
    this.ctx.quadraticCurveTo(xa, ya, xa + r, ya);
    this.ctx.fillStyle = this.backgroundColor;
    this.ctx.fill();
    this.ctx.closePath();
  }

  private drawVal (): void {
    if(!this.ctx) return;
    const margin = (this.badgeSize * this.marginScale) / TWO;
    this.ctx.beginPath();
    this.ctx.textBaseline = 'middle';
    this.ctx.textAlign = 'center';
    this.ctx.font = `bold ${this.badgeSize * this.fontScale}px Arial`;
    this.ctx.fillStyle = this.color;
    this.ctx.fillText(this.value, this.badgeSize / TWO + this.offset.x, this.badgeSize / TWO + this.offset.y + margin);
    this.ctx.closePath();
  }

  private drawFavicon (): void {
    if (!this.faviconEL) return;
    this.faviconEL.setAttribute('href', this.dataURL);
  }

  private draw (): void {
    if (+this.value) {
      this.drawIcon();
    } else {
      this.drawFullSizeIcon();
    };
    if (+this.value) this.drawShape();
    if (+this.value) this.drawVal();
    this.drawFavicon();
  }

  private setup (): void {
    // @ts-ignore
    this.faviconSize = this.img.naturalWidth / TWO;
    this.badgeSize = this.faviconSize * this.size;
    this.canvas.width = this.faviconSize;
    this.canvas.height = this.faviconSize;
    const sd = this.faviconSize - this.badgeSize;
    const sd2 = sd / TWO;

    //@ts-ignore
    this.offset = {
      n:  { x: sd2, y: ZERO },
      e:  { x: sd, y: sd2 },
      s:  { x: sd2, y: sd },
      w:  { x: ZERO, y: sd2 },
      nw: { x: ZERO, y: ZERO },
      ne: { x: sd, y: ZERO },
      sw: { x: ZERO, y: sd },
      se: { x: sd, y: sd },
    }[this.position];
  }
}

class BadgeNotificationService {
  private badge: Badge;
  private maxNotificationsNumber: number = MAXIMUM_BADGE_NOTIFICATIONS_NUMBER;
  private title: string = PRESENTER_TITLE;
  private titleEl: HTMLTitleElement | null;

  constructor () {
    this.badge = new Badge();
    this.titleEl = document.querySelector('title');
  }

  public setNotificationNumber = (value: number): void => {
    if(value > this.maxNotificationsNumber || value < 0) return;
    this.badge.setValue(value.toString());
    this.addNotificationNumberToTitle(value);
  };

  private addNotificationNumberToTitle = (notificationNumber: number): void => {
    if(!this.titleEl) return;

    if (notificationNumber <= 0) {
      this.titleEl.text = this.title;
    } else {
      this.titleEl.text = this.title + ` (${notificationNumber})`;
    }
  };
}

export { BadgeNotificationService, Badge };
